import { AccountInfo } from '@efwwit/integration-hub-sdk/dist/nodejs/types/identity';
import { Cancel, CheckCircle, DeleteForever, DriveFileRenameOutline, MoreTime, PauseCircle, PersonOff, PhonelinkSetup, PlayCircle, RemoveDone, Save, Sync, WarningRounded, Widgets } from "@mui/icons-material";
import { Avatar, Box, Button, ButtonGroup, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, Dropdown, List, ListItem, ListItemContent, ListItemDecorator, Menu, MenuButton, MenuItem, Modal, ModalDialog, Textarea, Tooltip, Typography } from "@mui/joy";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useGlobalContext } from "../../Context";
import { ImageGetter } from "../../utilities/assets";
import { Profile } from '../../utilities/identity';
import { FormSubmission } from "../AccountProfile/constructs";
import { UserRoles, getUserRoles } from "../utils";

type ModalAction = "reset" | "expire" | "extend" | "save" | "delete" | "sync" | "rename";
interface ModalProps {
    accounts: AccountInfo[];
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    actionOpen?: boolean;
    setActionOpen: Dispatch<SetStateAction<boolean>>;
    action?: ModalAction;
    overrideMessage: string;
    setOverrideMessage: Dispatch<SetStateAction<string>>;
}

interface ModalDescriptors {
    title: string;
    description: string;
    color?: "danger" | "neutral" | "primary" | "success" | "warning";
}

interface ActionModalProps {
    accounts: AccountInfo[];
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    action?: ModalAction;
    overrideMessage: string;
    formData?: FormSubmission;
}

interface ProfileImages {
    [key: string]: string;
}

export function WarningModal({ accounts, open, setOpen, action, setActionOpen, overrideMessage, setOverrideMessage }: ModalProps) {
    const { core, directReports } = useGlobalContext();
    const [images, setProfileImages] = useState<ProfileImages>({});
    const [descriptors, setDescriptors] = useState<ModalDescriptors>();

    const getProfileImage = async () => {
        if (accounts && core) {
            const loadedImages: ProfileImages = {};
            for (const user of accounts) {
                const image = await new ImageGetter(core).getPicture(user.id, (directReports || []).filter(profile => profile.id === user.id).length > 0, false);
                loadedImages[user.id] = image;
            }
            setProfileImages(loadedImages);
        }
    }

    useEffect(() => {
        switch (action) {
            case "reset":
                setDescriptors({
                    description: `
                    This action will remove all Multi Factor Authentication methods from the 
                    ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below. 
                    This will require configuring them again when they login next or may be asked to do so immediately.`.split(`\n`).join(""),
                    title: "Warning",
                    color: "warning"
                })
                break;
            case "expire":
                setDescriptors({
                    description: `
                    This action will immediately revoke access to the 
                    ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below. 
                    This means they will no longer have access to any EF systems or applications, including Teams and Email.`.split(`\n`).join(""),
                    title: "Warning",
                    color: "danger"
                })
                break;
            case "extend":
                setDescriptors({
                    description: `
                    This action will extend the 
                    ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below by 6 months, and if they are disabled, re-enable them.
                    After completion and a few minutes have passed, they will be able to login to their Teams and Email again.`.split(`\n`).join(""),
                    title: "Warning",
                    color: "success"
                })
                break;
            case "sync":
                setDescriptors({
                    description: `
                        This action will sync the
                        ${accounts.length > 1 ? accounts.length + " accounts" : "account"} with oracle, making sure it's up to date. If this process does not work, it indicates that there is something wrong with their profile on HCM.`.split(`\n`).join(""),
                    title: "Warning",
                    color: "success"
                })
                break;
            case "rename":
                setDescriptors({
                    description: `
                            This action will perform a full rename on the
                            ${accounts.length > 1 ? accounts.length + " accounts" : "account"}. This means their email and username will be updated to match their current first and last name on the system.
                            It's important to know that this will break ALL shared links through OneDrive and could cause other issues signing in to 3rd party systems.`.split(`\n`).join(""),
                    title: "Warning - Read carefully before you continue",
                    color: "danger"
                })
                break;
            case "delete":
                setDescriptors({
                    description: `
                        This action will permanently DELETE the
                        ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below effective immediately. Please only use this if you are certain the account should be deleted.`.split(`\n`).join(""),
                    title: "Warning",
                    color: "danger"
                })
                break;
            case "save":
                setDescriptors({
                    description: `
                    You are about to save the changes you have made to the below account, do you want to continue?`.split(`\n`).join(""),
                    title: "Warning",
                    color: "primary"
                })
                break;
        }
    }, [action, accounts])

    useEffect(() => {
        getProfileImage();
    }, [accounts]);

    return (
        <Modal open={open} onClose={() => setOpen(false)}>
            <ModalDialog invertedColors={(action === "delete" || action === "rename") ? true : false} variant={(action === "delete" || action === "rename") ? "solid" : "outlined"} color={descriptors?.color} role="alertdialog" maxWidth="sm" minWidth="sm" sx={{
                minHeight: "30vh"
            }}>
                <DialogTitle>
                    <WarningRounded />
                    {descriptors?.title}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {descriptors?.description}
                </DialogContent>
                <Divider />
                <DialogContent>
                    <List
                        aria-labelledby="ellipsis-list-demo"
                        sx={{
                            '--ListItemDecorator-size': '56px',
                            mb: 2,
                            mt: 2,
                            alignContent: "flex-end"
                        }}
                    >
                        {
                            accounts.map(user => {
                                return (
                                    <ListItem key={accounts.indexOf(user)}>
                                        <ListItemDecorator>
                                            <Avatar variant="solid" src={images[user.id]}>{user.displayName.split(" ").map((n) => n[0]).join("")}</Avatar>
                                        </ListItemDecorator>
                                        <ListItemContent>
                                            <Typography level="title-md">{user.displayName}</Typography>
                                            <Typography level="body-md" noWrap>
                                                {user.userPrincipalName}
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                )
                                // return <Typography>{user.userPrincipalName}</Typography>
                            })
                        }
                    </List>
                    {
                        action === "expire" &&
                        <>
                            <Typography>The following out of office will be set on the {`${accounts.length > 1 ? accounts.length + " accounts" : "account"}`}:</Typography>
                            <Typography color="danger">
                                {accounts.length > 1 ? "Please note, when expiring multiple accounts, the message is auto-generated for each account if nothing is entered below. Please leave this field empty if you want to use the default message." : ""}
                            </Typography>
                            <Textarea value={overrideMessage} required minRows={4} onChange={(e) => {
                                setOverrideMessage(e.currentTarget.value);
                            }} />
                        </>
                    }
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button onClick={() => {
                        setActionOpen(true)
                        setOpen(false);
                    }} color={descriptors?.color}>
                        {`${action ? action[0].toUpperCase() + action.substring(1) : "Ok"}`}
                    </Button>
                    <Button color="neutral" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal >
    )
}

type ActionState = "notStarted" | "inProgress" | "completeSuccess" | "completeFail";
interface ActionResult {
    [key: string]: ActionState;
}
interface LifecycleListItemProps {
    account: AccountInfo;
    actionResult: ActionResult
    errorMessage?: string;
}

export function LifecycleListItem({ account, actionResult, errorMessage }: LifecycleListItemProps) {
    switch (actionResult[account.id]) {
        case "notStarted":
            return (
                <ListItem id={`li-lifecycle-${account.id}`}>
                    <ListItemDecorator>
                        <CircularProgress color="warning" thickness={4} variant="soft" size="md"><PauseCircle /></CircularProgress>
                    </ListItemDecorator>
                    <ListItemContent>
                        <Typography level="title-sm">{account.displayName}</Typography>
                        <Typography level="body-sm" noWrap>
                            Preparing changes...
                        </Typography>
                    </ListItemContent>
                </ListItem>
            )
        case "inProgress":
            return (
                <ListItem id={`li-lifecycle-${account.id}`}>
                    <ListItemDecorator>
                        <CircularProgress color="neutral" thickness={4} variant="soft" size="md"><PlayCircle /></CircularProgress>
                    </ListItemDecorator>
                    <ListItemContent>
                        <Typography level="title-sm">{account.displayName}</Typography>
                        <Typography level="body-sm" noWrap>
                            Applying changes...
                        </Typography>
                    </ListItemContent>
                </ListItem>
            )
        case "completeSuccess":
            return (
                <ListItem id={`li-lifecycle-${account.id}`}>
                    <ListItemDecorator>
                        <CircularProgress color="success" value={100} thickness={4} variant="soft" size="md"><CheckCircle /></CircularProgress>
                    </ListItemDecorator>
                    <ListItemContent>
                        <Typography level="title-sm">{account.displayName}</Typography>
                        <Typography level="body-sm" noWrap>
                            Changes completed successfully.
                        </Typography>
                    </ListItemContent>
                </ListItem>
            )
        case "completeFail":
            return (
                <ListItem id={`li-lifecycle-${account.id}`}>
                    <ListItemDecorator>
                        <CircularProgress color="danger" value={100} thickness={4} variant="soft" size="md"><Cancel /></CircularProgress>
                    </ListItemDecorator>
                    <ListItemContent>
                        <Typography level="title-sm">{account.displayName}</Typography>
                        <Typography level="body-sm" color={errorMessage ? "warning" : "neutral"} noWrap>
                            {errorMessage ? `${errorMessage}` : "Changes have failed for this account."}
                        </Typography>
                    </ListItemContent>
                </ListItem>
            )
    }
}

export function PerformLifecycleActionModal({ accounts, open, setOpen, action, overrideMessage, formData }: ActionModalProps) {
    const [progress, setProgress] = useState<number>(0);
    const [actionResult, setActionResult] = useState<ActionResult>({});
    const { core, rootUser, directReports, setProfile, setDirectReports } = useGlobalContext();
    const [descriptors, setDescriptors] = useState<ModalDescriptors>();

    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const performAction = () => {
        const internalState: ActionResult = { ...actionResult };
        if (open && action && core) {
            switch (action) {
                case "reset":
                    for (const account of accounts) {
                        setTimeout(() => {
                            internalState[account.id] = "inProgress";
                            setActionResult({
                                ...actionResult,
                                ...internalState
                            });
                            Profile.resetMfa(core, account.id).then((result) => {
                                if (result.statusCode === 200) {
                                    internalState[account.id] = "completeSuccess";
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                } else {
                                    internalState[account.id] = "completeFail";
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                }
                            });
                        }, 500 * (accounts.indexOf(account) + 1));
                    }
                    break;
                case "expire":
                    for (const account of accounts) {
                        let message = `Hi, this mailbox is no longer active, please reach out to my manager for further queries.`;
                        if (!overrideMessage) {
                            if (directReports?.filter(user => user.id === account.id)) {
                                message = `Hi, this mailbox is no longer active, please reach out to ${rootUser?.mail} for further queries.`
                            } else {
                                message = `Hi, this mailbox is no longer active, please reach out to ${account.manager?.mail || "my manager"} for further queries.`
                            }
                        } else {
                            message = overrideMessage;
                        }
                        setTimeout(() => {
                            internalState[account.id] = "inProgress";
                            setActionResult({
                                ...actionResult,
                                ...internalState
                            });
                            Profile.expireAccount(core, account.id, message).then((result) => {
                                if (result.statusCode === 200) {
                                    if (rootUser && directReports && directReports.filter(dr => dr.id === account.id).length > 0) {
                                        Profile.reloadProfile(core, rootUser).then((profile) => {
                                            Profile.reloadDirectReports(core, rootUser).then((directReport) => {
                                                setProfile(profile);
                                                setDirectReports(directReport);
                                                internalState[account.id] = "completeSuccess";
                                                setActionResult({
                                                    ...actionResult,
                                                    ...internalState
                                                });
                                            });
                                        });
                                    } else {
                                        internalState[account.id] = "completeSuccess";
                                        setActionResult({
                                            ...actionResult,
                                            ...internalState
                                        });
                                    }
                                } else {
                                    internalState[account.id] = "completeFail";
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                }
                            });
                        }, 500 * (accounts.indexOf(account) + 1));
                    }
                    break;
                case "delete":
                    for (const account of accounts) {
                        setTimeout(() => {
                            internalState[account.id] = "inProgress";
                            setActionResult({
                                ...actionResult,
                                ...internalState
                            });
                            Profile.deleteAccount(core, account.id).then((result) => {
                                if (result.statusCode === 200) {
                                    if (rootUser && directReports && directReports.filter(dr => dr.id === account.id).length > 0) {
                                        Profile.reloadProfile(core, rootUser).then((profile) => {
                                            Profile.reloadDirectReports(core, rootUser).then((directReport) => {
                                                setProfile(profile);
                                                setDirectReports(directReport);
                                                internalState[account.id] = "completeSuccess";
                                                setActionResult({
                                                    ...actionResult,
                                                    ...internalState
                                                });
                                            });
                                        });
                                    } else {
                                        internalState[account.id] = "completeSuccess";
                                        setActionResult({
                                            ...actionResult,
                                            ...internalState
                                        });
                                    }
                                } else {
                                    internalState[account.id] = "completeFail";
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                }
                            });
                        }, 500 * (accounts.indexOf(account) + 1));
                    }
                    break;
                case "extend":
                    for (const account of accounts) {
                        setTimeout(() => {
                            internalState[account.id] = "inProgress";
                            setActionResult({
                                ...actionResult,
                                ...internalState
                            });
                            Profile.recertifyAccount(core, account.id).then((result) => {
                                if (result.statusCode === 200) {
                                    if (rootUser && directReports && directReports.filter(dr => dr.id === account.id).length > 0) {
                                        Profile.reloadProfile(core, rootUser).then((profile) => {
                                            Profile.reloadDirectReports(core, rootUser).then((directReport) => {
                                                setProfile(profile);
                                                setDirectReports(directReport);
                                                internalState[account.id] = "completeSuccess";
                                                setActionResult({
                                                    ...actionResult,
                                                    ...internalState
                                                });
                                            });
                                        });
                                    } else {
                                        internalState[account.id] = "completeSuccess";
                                        setActionResult({
                                            ...actionResult,
                                            ...internalState
                                        });
                                    }
                                } else {
                                    internalState[account.id] = "completeFail";
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                }
                            });
                        }, 500 * (accounts.indexOf(account) + 1));
                    }
                    break;
                case "sync":
                    for (const account of accounts) {
                        setTimeout(() => {
                            internalState[account.id] = "inProgress";
                            setActionResult({
                                ...actionResult,
                                ...internalState
                            });
                            Profile.syncWithOracleHCM(core, account.employeeId).then((result) => {
                                if (result.statusCode === 200) {
                                    if (rootUser && directReports && directReports.filter(dr => dr.id === account.id).length > 0) {
                                        Profile.reloadProfile(core, rootUser).then((profile) => {
                                            Profile.reloadDirectReports(core, rootUser).then((directReport) => {
                                                setProfile(profile);
                                                setDirectReports(directReport);
                                                internalState[account.id] = "completeSuccess";
                                                setActionResult({
                                                    ...actionResult,
                                                    ...internalState
                                                });
                                            });
                                        });
                                    } else {
                                        internalState[account.id] = "completeSuccess";
                                        setActionResult({
                                            ...actionResult,
                                            ...internalState
                                        });
                                    }
                                } else {
                                    internalState[account.id] = "completeFail";
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                }
                            });
                        }, 500 * (accounts.indexOf(account) + 1));
                    }
                    break;
                case "rename":
                    for (const account of accounts) {
                        setTimeout(() => {
                            internalState[account.id] = "inProgress";
                            setActionResult({
                                ...actionResult,
                                ...internalState
                            });
                            Profile.renameAccount(core, account.employeeId).then((result) => {
                                if (result.statusCode === 200) {
                                    if (rootUser && directReports && directReports.filter(dr => dr.id === account.id).length > 0) {
                                        Profile.reloadProfile(core, rootUser).then((profile) => {
                                            Profile.reloadDirectReports(core, rootUser).then((directReport) => {
                                                setProfile(profile);
                                                setDirectReports(directReport);
                                                internalState[account.id] = "completeSuccess";
                                                setActionResult({
                                                    ...actionResult,
                                                    ...internalState
                                                });
                                            });
                                        });
                                    } else {
                                        internalState[account.id] = "completeSuccess";
                                        setActionResult({
                                            ...actionResult,
                                            ...internalState
                                        });
                                    }
                                } else {
                                    internalState[account.id] = "completeFail";
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                }
                            });
                        }, 500 * (accounts.indexOf(account) + 1));
                    }
                    break;
                case "save":
                    for (const account of accounts) {
                        setTimeout(() => {
                            internalState[account.id] = "inProgress";
                            setActionResult({
                                ...actionResult,
                                ...internalState
                            });
                            Profile.updateAccount(core, account.id, formData).then((result) => {
                                if (result.statusCode === 200) {
                                    if (rootUser && directReports && directReports.filter(dr => dr.id === account.id).length > 0) {
                                        Profile.reloadProfile(core, rootUser).then((profile) => {
                                            Profile.reloadDirectReports(core, rootUser).then((directReport) => {
                                                setProfile(profile);
                                                setDirectReports(directReport);
                                                internalState[account.id] = "completeSuccess";
                                                setActionResult({
                                                    ...actionResult,
                                                    ...internalState
                                                });
                                            });
                                        });
                                    } else {
                                        internalState[account.id] = "completeSuccess";
                                        setActionResult({
                                            ...actionResult,
                                            ...internalState
                                        });
                                    }
                                } else {
                                    internalState[account.id] = "completeFail";
                                    setErrorMessage(result.body ? result.body : (result as any).message);
                                    setActionResult({
                                        ...actionResult,
                                        ...internalState
                                    });
                                }
                            })
                        }, 500 * (accounts.indexOf(account) + 1));
                    }
                    break;
            }
        }
    }

    const resetState = () => {
        setErrorMessage(undefined);
        const localActionState: ActionResult = {};
        setActionResult({});
        for (const account of accounts) {
            localActionState[account.id] = "notStarted";
        }
        setActionResult({
            ...localActionState
        });
    }

    useEffect(() => {
        let completeCount = 0;
        for (const id of Object.keys(actionResult)) {
            if (actionResult[id] === "completeFail" || actionResult[id] === "completeSuccess") {
                completeCount++;
            }
        }
        setProgress(completeCount * 100 / accounts.length);
    }, [actionResult]);

    useEffect(() => {
        if (open) {
            performAction();
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            resetState();
        }
    }, [open, accounts]);

    useEffect(() => {
        switch (action) {
            case "reset":
                setDescriptors({
                    description: `Please wait while we reset the Multi Factor Authentication methods on the ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below.`,
                    title: "Multi Factor Authentication Reset"
                })
                break;
            case "sync":
                setDescriptors({
                    description: `Please wait while we sync the ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below with Oracle HCM.`,
                    title: "Oracle account Sync"
                })
                break;
            case "expire":
                setDescriptors({
                    description: `Please wait while we expire the ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below.`,
                    title: "Expire"
                })
                break;
            case "rename":
                setDescriptors({
                    description: `Please wait while we perform a full rename on the ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below.`,
                    title: "Rename"
                })
                break;
            case "delete":
                setDescriptors({
                    description: `Please wait while we delete the ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below.`,
                    title: "Delete"
                })
                break;
            case "extend":
                setDescriptors({
                    description: `Please wait while we extend the ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below.`,
                    title: "Extend"
                })
                break;
            case "save":
                setDescriptors({
                    description: `Please wait while we save the changes you have made on the ${accounts.length > 1 ? accounts.length + " accounts" : "account"} below.`,
                    title: "Saving Changes"
                })
                break;
        }
    }, [action, accounts]);

    return (
        <Modal open={open} onClose={() => setOpen(true)} >
            <ModalDialog color='neutral' role="alertdialog" maxWidth="sm" minWidth="sm" sx={{
                minHeight: "30vh"
            }}>
                <DialogTitle sx={{
                    display: "flex",
                    justifyContent: "space-between"
                }}>

                    <Box>{descriptors?.title}</Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {progress !== 100 ? descriptors?.description : "All action have  now been completed, any failed changes will be shown in red below."}
                </DialogContent>
                <Divider />
                <DialogContent>
                    <List sx={{ '--ListItemDecorator-size': '56px' }}>
                        {
                            accounts.map(user => {
                                return (
                                    <LifecycleListItem key={`lifecycle-li-root-${accounts.indexOf(user)}`} account={user} actionResult={actionResult} errorMessage={errorMessage}></LifecycleListItem>
                                )
                            })
                        }
                    </List>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button onClick={() => {
                        resetState();
                        setOpen(false);
                    }} disabled={progress !== 100}>Done</Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}

interface ProfileActionsProps {
    isValid: boolean;
    accounts: AccountInfo[];
    setAccounts?: Dispatch<SetStateAction<AccountInfo[]>>;
    multiSelect?: boolean;
    formData?: FormSubmission;
}

export default function ProfileActions({ isValid, accounts, multiSelect, formData, setAccounts }: ProfileActionsProps) {
    const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
    const [actionModalOpen, setActionModalOpen] = useState<boolean>(false);
    const [action, setAction] = useState<ModalAction>();

    const { core, rootUser, directReports } = useGlobalContext();
    const [userRoles, setUserRoles] = useState<UserRoles>();

    const [overrideMessage, setOverrideMessage] = useState<string>("");

    useEffect(() => {
        if (rootUser && directReports) {
            setUserRoles(getUserRoles(rootUser, directReports, accounts));
        }
        if (accounts.length == 1) {
            let message = `Hi, this mailbox is no longer active, please reach out to my manager for further queries.`;
            if (directReports?.filter(user => user.id === accounts[0]?.id)) {
                message = `Hi, this mailbox is no longer active, please reach out to ${rootUser?.mail} for further queries.`
            } else {
                message = `Hi, this mailbox is no longer active, please reach out to ${accounts[0]?.manager?.mail || "my manager"} for further queries.`
            }
            setOverrideMessage(message);
        } else {
            setOverrideMessage("");
        }
    }, [accounts, core]);

    return (
        <>
            <Box sx={{
                alignContent: "center",
                justifyContent: {
                    lg: "space-between",
                },
                display: "flex"
            }}>
                {/* Below forces the height of other buttons to be LG without inflating them. */}
                <Button disabled variant="soft" color="neutral" size="lg" sx={{
                    visibility: "hidden",
                    width: "1px !important",
                    maxWidth: "1px !important",
                    pl: 0,
                    pr: 0
                }} />

                <Box></Box>
                <Dropdown sx={{
                    display: {
                        xs: "flex",
                        lg: "none"
                    }
                }}>
                    <ButtonGroup size="md" variant="soft" aria-label="tooltip button group" sx={multiSelect ? {
                        width: "auto",
                        display: {
                            xs: "flex",
                            lg: "none"
                        },
                        ml: 1,
                        "--ButtonGroup-separatorColor": "var(--joy-palette-primary)"
                    } : {
                        width: "100%",
                        display: {
                            xs: "flex",
                            lg: "none"
                        },
                        ml: 1,
                        "--ButtonGroup-separatorColor": "var(--joy-palette-primary)"
                    }}>
                        <Button sx={multiSelect ? {
                            width: "auto"
                        } : {
                            width: "50%"
                        }} key={"btn_text"} disabled color="neutral">Actions{accounts.length > 0 && multiSelect ?
                            <><Chip variant="solid" sx={{ ml: 1, mr: 1 }} color="primary">{accounts.length}</Chip> Selected</> :
                            ""
                            }</Button>
                        <MenuButton color="primary" variant="solid" sx={{
                            width: "50%",
                            display: {
                                xs: "flex",
                                lg: "none"
                            }
                        }}><Widgets /></MenuButton>
                    </ButtonGroup>

                    <Menu>
                        {
                            setAccounts && <MenuItem onClick={() => setAccounts([])}><Button color="primary"><RemoveDone /></Button> Clear Selection</MenuItem>
                        }
                        <MenuItem disabled={!userRoles?.canResetMFA} onClick={() => {
                            setAction("reset");
                            setWarningModalOpen(true);
                        }} >
                            <Button disabled={!userRoles?.canResetMFA} color="warning"><PhonelinkSetup /></Button>
                            Reset MFA
                        </MenuItem>
                        <MenuItem disabled={!userRoles?.canExpire} onClick={() => {
                            setAction("expire");
                            setWarningModalOpen(true);
                        }} >
                            <Button disabled={!userRoles?.canExpire} color="danger"><PersonOff /></Button>
                            Expire Account
                        </MenuItem>
                        {(rootUser?.RBAC?.iaaRole?.includes("globalAdmin") && !(accounts.filter(account => account.id === rootUser.id).length > 0)) &&
                            <MenuItem disabled={!rootUser?.RBAC?.iaaRole?.includes("globalAdmin")} onClick={() => {
                                setAction("delete");
                                setWarningModalOpen(true);
                            }} >
                                <Button disabled={!(rootUser?.RBAC?.iaaRole?.includes("globalAdmin") && !(accounts.filter(account => account.id === rootUser.id).length > 0))} color="danger"><DeleteForever /></Button>
                                Delete Account
                            </MenuItem>
                        }
                        {(rootUser?.RBAC?.iaaRole?.includes("globalAdmin") && !(accounts.filter(account => account.id === rootUser.id).length > 0)) && <MenuItem disabled={!userRoles?.canExtend} onClick={() => {
                            setAction("rename");
                            setWarningModalOpen(true);
                        }} >
                            <Button disabled={!userRoles?.canExtend} color="danger"><DriveFileRenameOutline /></Button>
                            Rename Account
                        </MenuItem>
                        }

                        <MenuItem disabled={!userRoles?.canExtend} onClick={() => {
                            setAction("extend");
                            setWarningModalOpen(true);
                        }}>
                            <Button disabled={!userRoles?.canExtend} color="success"><MoreTime /></Button>
                            Extend Account
                        </MenuItem>

                        <MenuItem disabled={accounts.find(acc => acc.Basic?.integrationPlatform !== "hcm") ? true : false} onClick={() => {
                            setAction("sync");
                            setWarningModalOpen(true);
                        }}>
                            <Button disabled={accounts.find(acc => acc.Basic?.integrationPlatform !== "hcm") ? true : false} color="success"><Sync /></Button>
                            Sync with Oracle
                        </MenuItem>

                        <MenuItem disabled={!isValid ? true : (multiSelect ? true : false) || !userRoles?.canSave} onClick={() => {
                            setAction("save");
                            setWarningModalOpen(true);
                        }}>
                            <Button type="submit" form="profile-form" disabled={!isValid ? true : (multiSelect ? true : false) || !userRoles?.canSave} color="primary"><Save /></Button>
                            Save Changes
                        </MenuItem>
                    </Menu>
                </Dropdown>
                <Box display={"flex"} sx={{
                    display: {
                        xs: "none",
                        lg: "flex"
                    }
                }}>
                    <ButtonGroup size="md" variant="soft" aria-label="tooltip button group" sx={{
                        "--ButtonGroup-separatorColor": "var(--joy-palette-primary)"
                    }}>
                        <Button key={"btn_text"} disabled color="neutral">Actions{accounts.length > 0 && multiSelect ?
                            <><Chip variant="solid" sx={{ ml: 1, mr: 1 }} color="primary">{accounts.length}</Chip> Selected</> :
                            ""
                        }</Button>

                        {
                            setAccounts && <Tooltip arrow title="Clear Selection" color="neutral">
                                <Button onClick={() => setAccounts([])} color="primary"><RemoveDone /></Button>
                            </Tooltip>
                        }
                        {(rootUser?.RBAC?.iaaRole?.includes("globalAdmin") && !(accounts.filter(account => account.id === rootUser.id).length > 0)) &&
                            <Tooltip arrow title="Delete" color="danger">
                                <Button disabled={!rootUser?.RBAC?.iaaRole?.includes("globalAdmin")} onClick={() => {
                                    setAction("delete");
                                    setWarningModalOpen(true);
                                }} color="danger" variant="solid"><DeleteForever /></Button>
                            </Tooltip>
                        }
                        <Tooltip arrow title="Expire" color="danger">
                            <Button disabled={!userRoles?.canExpire} onClick={() => {
                                setAction("expire");
                                setWarningModalOpen(true);
                            }} color="danger"><PersonOff /></Button>
                        </Tooltip>
                        {(rootUser?.RBAC?.iaaRole?.includes("globalAdmin") && !(accounts.filter(account => account.id === rootUser.id).length > 0)) &&
                            <Tooltip arrow title="Rename Account" color="danger">
                                <Button disabled={!userRoles?.canExpire} onClick={() => {
                                    setAction("rename");
                                    setWarningModalOpen(true);
                                }} color="danger"><DriveFileRenameOutline /></Button>
                            </Tooltip>
                        }
                        <Tooltip arrow title="Extend" color="success">
                            <Button disabled={!userRoles?.canExtend} onClick={() => {
                                setAction("extend");
                                setWarningModalOpen(true);
                            }} color="success"><MoreTime /></Button>
                        </Tooltip>
                        <Tooltip arrow title="Reset MFA" color="warning">
                            <Button disabled={!userRoles?.canResetMFA} onClick={() => {
                                setAction("reset");
                                setWarningModalOpen(true);
                            }} color="warning"><PhonelinkSetup /></Button>
                        </Tooltip>
                        <Tooltip arrow title="Sync with Oracle" color="success">
                            <Button disabled={accounts.find(acc => acc.Basic?.integrationPlatform !== "hcm") ? true : false} onClick={() => {
                                setAction("sync");
                                setWarningModalOpen(true);
                            }} color="success"><Sync /></Button>
                        </Tooltip>
                        <Tooltip arrow title="Save" color="primary">
                            <Button type="submit" form="profile-form" onClick={() => {
                                setAction("save");
                                setWarningModalOpen(true);
                            }} disabled={!isValid ? true : (multiSelect ? true : false) || !userRoles?.canSave} color="primary"><Save /></Button>
                        </Tooltip>
                    </ButtonGroup>
                </Box>
            </Box>
            <WarningModal overrideMessage={overrideMessage} setOverrideMessage={setOverrideMessage} accounts={accounts} open={warningModalOpen} setOpen={setWarningModalOpen} actionOpen={actionModalOpen} setActionOpen={setActionModalOpen} action={action}></WarningModal>
            <PerformLifecycleActionModal formData={formData} overrideMessage={overrideMessage} accounts={accounts} open={actionModalOpen} setOpen={setActionModalOpen} action={action} ></PerformLifecycleActionModal>
        </>
    )
}